import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { apiFetch } from "@rook/shared/lib/apiFetch";

import { fetchSession, type Session } from "./session";

/**
 * Auth context for the SPA.
 *
 * Three things this owns:
 *
 *   1. The `session` query — `GET /api/auth/session` via TanStack
 *      Query. Cached, deduped across components, refetched on focus.
 *      Initial probe runs once on cold mount (regardless of which
 *      route the user landed on).
 *
 *   2. `refresh()` — re-fetch the session. Called after a successful
 *      login so AuthGuard immediately sees the new state without
 *      waiting for the focus refetch. Also called by the auth:logout
 *      event listener below to invalidate cached state on the way
 *      out.
 *
 *   3. The `auth:logout` window event listener. `apiFetch` fires
 *      this when a request comes back 401 even after a refresh
 *      attempt. The listener navigates to `/` so the user re-auths.
 *      Decoupled via window event so `apiFetch` doesn't need to know
 *      about React context — it can be called from anywhere.
 */

type AuthContextValue = {
  session: Session;
  isLoading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_QUERY_KEY = ["auth", "session"] as const;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    // The session expiry drives revalidation cadence elsewhere; here
    // a 1-minute stale time is fine — most route changes resolve
    // from cache.
    staleTime: 60_000,
    // Avoid retrying the session probe on a 4xx — a real 401 means
    // "no session", which is a valid answer not an error.
    retry: false,
  });
  // NOTE: do NOT add `placeholderData: { ok: false }` here. An earlier
  // version of this hook carried it as a convenience so consumers
  // could read `session.ok` without handling `undefined`. The
  // side-effect: TanStack Query treats placeholderData as "loaded",
  // so on cold mount `isLoading` is FALSE and `data.ok` is FALSE —
  // AuthGuard/GuestOnly read that as "not authenticated", redirect
  // to `/`, the LoginView paints for ~200ms, then the real
  // `/api/auth/session` response arrives and the guard re-routes
  // to the protected view. Pepe reported this as the "login flash"
  // bug on 2026-05-21. Removing `placeholderData` makes `isLoading`
  // STAY true during the cold probe so the guard correctly shows
  // the "Checking session…" splash instead of flashing login.
  // Consumers (LoginView, SignupView, Topbar) read session ONLY
  // after the guard has gated render — they never see undefined
  // in practice. The `session: data ?? { ok: false }` fallback in
  // the useMemo below covers the type contract.

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }, [queryClient]);

  const logout = useCallback(async () => {
    // Best-effort BFF-side logout (revokes Cognito tokens, clears
    // the HttpOnly cookie). The browser-visible action ("I'm logged
    // out") must succeed regardless of whether the Cognito Revoke
    // call did — otherwise a transient 5xx blocks logout. Same
    // defensive posture as `auth-logout/handler.ts`.
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } catch {
      // swallow
    }
    queryClient.setQueryData<Session>(SESSION_QUERY_KEY, { ok: false });
    navigate("/", { replace: true });
  }, [queryClient, navigate]);

  // Listen for `auth:logout` from `apiFetch` (fires when a 401
  // survives the refresh-and-retry loop). React-side: invalidate
  // cached session + bounce to /.
  useEffect(() => {
    const onLogout = () => {
      queryClient.setQueryData<Session>(SESSION_QUERY_KEY, { ok: false });
      navigate("/", { replace: true });
    };
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, [navigate, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session: data ?? { ok: false },
      isLoading,
      refresh,
      logout,
    }),
    [data, isLoading, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
};
